import React, { useRef, useState } from 'react';
import { shopDueDate } from '../../../ShopShipDate';
import {
	Alert,
	Button,
	Card,
	Form,
	InputNumber,
	Modal,
	notification,
	Tag,
	Typography,
	Row,
	Col,
	Checkbox,
	Select,
} from 'antd';
import { CloseOutlined, PlusOutlined, TruckFilled } from '@ant-design/icons';

import { Lineitem } from '../../../../interfaces/Ordel.Interface';
import DeliveryList from '../DeliveryList';

import {
	BatchSibling,
	useCreateDeliveryMutation,
	useGetBatchSiblingsQuery,
	useGetRelatedMaterialReservationsQuery,
	useLazyGetInspectionSampleLabelingQuery,
} from '../../../../redux/api/order-line-item.ts';
import {
	useGetUsersQuery,
	UserQueryInterface,
} from '../../../../redux/api/users.ts';
import { usePrintLocation } from '../../../../context/PrintLocationContext';
import OperatorPinField from '../../../Operators/OperatorPinField';
import {
	InspectionSampleLabelingTag,
	confirmDeliverySampleLabeling,
} from '../../../InspectionSampleLabeling';
import { confirmCleaning } from '../../../ProcessCatalog/confirmCleaning';
import CleaningRequirementTag from '../../../ProcessCatalog/CleaningRequirementTag';

const { Meta } = Card;

const { Text, Title } = Typography;

function jobIsFull(item: Lineitem) {
	// A quantity of 0 has nothing left and nothing ordered. That is not a
	// full job, and the notice must not say it is.
	return item.quantity > 0 && item.remaining_quantity === 0;
}

function siblingDue(sibling: BatchSibling) {
	return (
		sibling.required_partner_ship_date ||
		sibling.required_part_ship_date ||
		'no ship date'
	);
}

function OpenBatchNotice({
	itemId,
	thisJobFull,
}: {
	itemId: string;
	thisJobFull: boolean;
}) {
	const { data } = useGetBatchSiblingsQuery({ id: itemId });
	const open = (data?.siblings || []).filter(
		(sibling) => sibling.remaining_quantity > 0
	);
	if (!open.length) return null;
	return (
		<Alert
			type="warning"
			showIcon
			style={{ marginTop: 8, textAlign: 'left' }}
			message={thisJobFull ? 'This job is full' : 'Same part on other jobs'}
			description={
				<ul style={{ paddingLeft: 18, margin: 0 }}>
					{open.map((sibling) => (
						<li key={sibling.id}>
							{siblingDue(sibling)} — {sibling.remaining_quantity} left of{' '}
							{sibling.quantity}
						</li>
					))}
				</ul>
			}
		/>
	);
}

interface IProps {
	item: Lineitem;
	refetch?: () => void;
}

interface IModalProps {
	item: Lineitem;
	handleCancel: () => void;
	handleSuccess: () => void;
}

const ShippingModal = ({ item, handleSuccess, handleCancel }: IModalProps) => {
	const [form] = Form.useForm();

	const [createDeliveryMutation] = useCreateDeliveryMutation();
	const [fetchSampleLabeling] = useLazyGetInspectionSampleLabelingQuery();
	const { promptForBuilding } = usePrintLocation();
	const [isSaving, setIsSaving] = useState(false);
	const [isConfirmVisible, setIsConfirmVisible] = useState(false);
	const [isConfirmed, setIsConfirmed] = useState(false);
	const [operatorTouched, setOperatorTouched] = useState(false);
	// One submission per confirmation. Everything between the operator pressing
	// OK and isSaving turning the button into a spinner is async -- the labeling
	// fetch and its own modal -- so a double tap on a tablet used to stack two
	// full submissions, and the server allows over-delivery, so both committed
	// and both printed a tag. A ref rather than state because two taps in the
	// same tick would both read the same stale value. Over-delivery is still
	// allowed when this part has no other open job; a sibling that still needs
	// pieces makes the server refuse it.
	const submitInFlight = useRef(false);

	const userQuery: UserQueryInterface = { groups__name: 'Machine Operators' };
	const { data: userData } = useGetUsersQuery(userQuery);
	const { data: materialReservationsData } =
		useGetRelatedMaterialReservationsQuery(item.id);

	const handleSubmitWithBuilding = async (
		values: {
			user_id?: number;
			delivered_quantity: number;
			scrapped_quantity: number;
			returned_quantity: number;
		},
		buildingId: string,
		confirmedSampleNumbers?: number[],
		cleaningAcknowledged?: boolean,
	) => {
		setIsSaving(true);

		const result: any = await createDeliveryMutation({
			id: item.id,
			params: {
				...values,
				...(buildingId ? { building_id: buildingId } : {}),
				// What the operator actually read and initialed. The server
				// recomputes the attribution and only logs the difference, so this
				// is evidence, not input (PTB-6619).
				...(confirmedSampleNumbers
					? { confirmed_inspection_sample_numbers: confirmedSampleNumbers }
					: {}),
				// The server refuses the delivery without this whenever the job's
				// cleaning is special, so it is a gate rather than evidence.
				...(cleaningAcknowledged
					? { cleaning_instructions_acknowledged: true }
					: {}),
			},
		});

		setIsSaving(false);
		// Released whatever the outcome: a rejected delivery has to stay
		// retryable from the modal the operator is still looking at.
		submitInFlight.current = false;

		if ('error' in result) {
			const error = result.error as any;
			const raw = (error && (error.data ?? error.error)) ?? error;
			// The server is the source of truth for the cleaning gate. A card
			// loaded before a special cleaning was applied carries no
			// cleaning_requirement, so the pre-submit question was never asked;
			// without this branch the operator is dead-ended on a 400 loop
			// until they reload the app. Ask the question with the server's own
			// payload and resubmit once, acknowledged.
			if (
				!cleaningAcknowledged &&
				raw?.cleaning_confirmation_required &&
				raw?.cleaning
			) {
				const cleaned = await confirmCleaning(raw.cleaning);
				if (!cleaned) {
					notification.warning({
						message: 'Delivery not recorded',
						description: `Clean the parts as required (${raw.cleaning.name}), then record the delivery again.`,
					});
					return;
				}
				submitInFlight.current = true;
				await handleSubmitWithBuilding(
					values,
					buildingId,
					confirmedSampleNumbers,
					true,
				);
				return;
			}
			const apiMessage =
				typeof raw === 'string'
					? raw
					: raw?.error || raw?.message || raw?.detail || '';
			const text = String(apiMessage).trim();
			if (raw?.code === 'BATCH_QUANTITY_EXCEEDED') {
				const siblings = (raw.siblings || []) as BatchSibling[];
				Modal.error({
					centered: true,
					title: 'This job is already at its quantity',
					content: (
						<div>
							<p>{text}</p>
							{siblings.length > 0 && (
								<ul style={{ paddingLeft: 18, marginBottom: 0 }}>
									{siblings.map((sibling) => (
										<li key={sibling.id}>
											{siblingDue(sibling)} — {sibling.remaining_quantity} left
											of {sibling.quantity}
										</li>
									))}
								</ul>
							)}
						</div>
					),
				});
			} else if (
				text.toLowerCase().includes('quality measurements are not complete')
			) {
				Modal.confirm({
					centered: true,
					title: 'Quality Measurements Pending',
					content:
						'Quality must be completed before the final delivery. Go to the Quality section to complete measurements now.',
					okText: 'Go to Quality',
					cancelText: 'Cancel',
					okButtonProps: {
						type: 'primary',
						style: { backgroundColor: '#1677ff', color: '#fff' },
					},
					onOk: () => {
						const url = `/orders/line-items/${item.id}?tab=quality`;
						window.open(url, '_blank');
					},
				});
			} else {
				notification.error({
					duration: 8,
					message: 'Delivery Failed',
					description: text
						? text
						: 'An error occurred while creating the delivery. Please try again.',
					placement: 'topRight',
				});
			}
			return;
		}
		notification.success({
			duration: 10,
			message: 'Success',
			description: 'The item has been shipped successfully.',
			placement: 'topRight',
		});
		setIsConfirmVisible(false);
		setIsConfirmed(false);
		handleSuccess();
	};

	const onFinish = async (values: {
		user_id?: number;
		delivered_quantity: number;
		scrapped_quantity: number;
		returned_quantity: number;
	}) => {
		if (!values.user_id) {
			setOperatorTouched(true);
			notification.warning({
				message: 'Operator Not Identified',
				description: 'Please enter your PIN before submitting.',
			});
			return;
		}

		// Last point where the operator can still be stopped: the delivery tag
		// prints server-side at the end of create-delivery, so anything after
		// this is already committed (PTB-6619). Fetched here rather than on
		// modal open so a sample completed in the meantime is included, and so
		// samples an earlier partial delivery already covered are excluded.
		let confirmedSampleNumbers: number[] | undefined;
		if (item.requires_inspection_sample_labeling) {
			let sampleNumbers: number[] | undefined;
			try {
				const state = await fetchSampleLabeling({ id: item.id }).unwrap();
				sampleNumbers = state.inspected_sample_numbers ?? [];
			} catch {
				// The question still has to be asked, just without the numbers.
			}

			const labeled = await confirmDeliverySampleLabeling(
				sampleNumbers ?? []
			);
			if (!labeled) {
				submitInFlight.current = false;
				notification.warning({
					message: 'Delivery not recorded',
					description:
						'Bag and label the samples, then record the delivery again.',
				});
				return;
			}
			// Left undefined when the fetch failed: the operator was shown no
			// numbers, so sending an empty list would have the server log a
			// divergence against them for numbers they were never offered.
			confirmedSampleNumbers = sampleNumbers;
		}

		// Asked after the labeling question and before the building picker, so
		// the two attestations are back to back and neither is separated from the
		// submit by the print-location step. Absent on a standard cleaning, which
		// is the overwhelming majority of jobs.
		let cleaningAcknowledged: boolean | undefined;
		if (item.cleaning_requirement) {
			const cleaned = await confirmCleaning(item.cleaning_requirement);
			if (!cleaned) {
				submitInFlight.current = false;
				notification.warning({
					message: 'Delivery not recorded',
					description: `Clean the parts as required (${item.cleaning_requirement.name}), then record the delivery again.`,
				});
				return;
			}
			cleaningAcknowledged = true;
		}

		promptForBuilding(
			(buildingId) =>
				handleSubmitWithBuilding(
					values,
					buildingId,
					confirmedSampleNumbers,
					cleaningAcknowledged,
				),
			{
				okText: 'Submit and Print',
				description: 'Select the building to print the delivery tags at.',
			},
		);
	};

	const handlePreSubmit = async () => {
		if (!form.getFieldValue('user_id')) {
			setOperatorTouched(true);
			notification.warning({
				message: 'Operator Not Identified',
				description: 'Please enter your PIN before submitting.',
			});
			return;
		}
		// Quantities used to be prefilled, so this almost always passed. They
		// start empty now, and validating only inside form.submit() meant the
		// operator signed the FOD declaration before being told a field was
		// missing. Reject here so the declaration never opens on a bad form.
		try {
			await form.validateFields();
		} catch {
			return;
		}
		// Cleared on every fresh attempt: an abandoned submission -- the operator
		// declines the labeling question, or cancels the building picker, which
		// has no callback to hang a release on -- must not lock the next one out.
		// isConfirmed only otherwise resets on cancel or success, so a failed
		// attempt left the declaration already ticked on the next open.
		setIsConfirmed(false);
		submitInFlight.current = false;
		setIsConfirmVisible(true);
	};

	const handleConfirmSubmit = () => {
		if (!isConfirmed || submitInFlight.current) {
			return;
		}
		submitInFlight.current = true;
		// Closed on the way out rather than on success: a button that is gone
		// cannot be pressed twice, and a button that is still there but does
		// nothing is worse than either. The delivery modal behind it keeps
		// showing the spinner.
		setIsConfirmVisible(false);
		form.submit();
	};

	const returnedQuantity = Form.useWatch('returned_quantity', form);
	const operatorId = Form.useWatch('user_id', form);

	return (
		<>
			<Modal
				width={1000}
				open={true}
				centered
				onCancel={handleCancel}
				onOk={form.submit}
				style={{
					minHeight: '300px',
				}}
				styles={{
					content: {
						// vh, not dvh: shop-floor WebViews older than Chrome 108
						// or Safari 15.4 ignore dvh and would drop this cap.
						maxHeight: 'calc(100vh - 48px)',
						display: 'flex',
						flexDirection: 'column',
					},
					body: {
						minHeight: 0,
						overflowY: 'auto',
					},
				}}
				footer={[
					<Button key="cancel" onClick={handleCancel}>
						Cancel
					</Button>,
					<Button
						key="submit"
						type="primary"
						onClick={handlePreSubmit}
						loading={isSaving}
					>
						Submit and Print
					</Button>,
				]}
			>
				<Row>
					{item.requires_inspection_sample_labeling && (
						<Col span={24} style={{ marginBottom: 12 }}>
							<InspectionSampleLabelingTag />
						</Col>
					)}
					{/* Shown on the form as well as in the confirmation, so the
					    operator can go and clean the parts before filling in
					    quantities rather than being stopped at the end. */}
					{item.cleaning_requirement && (
						<Col span={24} style={{ marginBottom: 12 }}>
							<CleaningRequirementTag
								cleaning={item.cleaning_requirement}
							/>
						</Col>
					)}
					<Col span={24}>
						<div>
							<Title level={5}>Previous Delivered Quantities</Title>
						</div>
						<div
							data-testid="delivery-history-scroll"
							style={{
								maxHeight: 'min(420px, 45vh)',
								overflowY: 'auto',
							}}
						>
							<OpenBatchNotice
								itemId={item.id}
								thisJobFull={jobIsFull(item)}
							/>
							<DeliveryList item={item} showReprint onChanged={handleSuccess} />
						</div>
					</Col>

					<Col span={24}>
						<div>
							<Title level={5}>Add New Delivery</Title>
						</div>
						<Form
							style={{
								width: '100%',
								marginTop: '20px',
							}}
							form={form}
							onFinish={(values) => onFinish(values)}
							layout="vertical"
						>
							<Row gutter={16}>
								<Col span={12}>
									{/* Matches Form.Item's bottom margin so the fields that
									    wrap below this taller card keep the same rhythm. */}
									<div style={{ marginBottom: 24 }}>
										<OperatorPinField
											userData={userData?.results || []}
											expectedGroups={['Machine Operators']}
											value={operatorId}
											onResolved={(userId) =>
												form.setFieldsValue({ user_id: userId })
											}
											showError={operatorTouched}
										/>
									</div>
									<Form.Item name="user_id" hidden>
										<InputNumber />
									</Form.Item>
								</Col>
								<Col span={12}>
									<Form.Item
										label="Quantity Delivered"
										rules={[
											{
												required: true,
												message: 'Please input the shipped quantity!',
											},
											{
												validator: (_, value) => {
													if (
														value === undefined ||
														value === null ||
														value === ''
													)
														return Promise.resolve();
													const num = Number(value);
													if (!Number.isInteger(num) || num < 0) {
														return Promise.reject(
															new Error(
																'Quantity must be a whole number (no decimals).'
															)
														);
													}
													return Promise.resolve();
												},
											},
										]}
										name="delivered_quantity"
									>
										<InputNumber
											min={0}
											step={1}
											precision={0}
											style={{ width: '100%' }}
										/>
									</Form.Item>
								</Col>
								<Col span={12}>
									<Form.Item
										label="Quantity Parts Scrapped"
										rules={[
											{
												required: true,
												message: 'Please input the scrapped quantity!',
											},
											{
												validator: (_, value) => {
													if (
														value === undefined ||
														value === null ||
														value === ''
													)
														return Promise.resolve();
													const num = Number(value);
													if (!Number.isInteger(num) || num < 0) {
														return Promise.reject(
															new Error(
																'Quantity must be a whole number (no decimals).'
															)
														);
													}
													return Promise.resolve();
												},
											},
										]}
										name="scrapped_quantity"
									>
										<InputNumber
											min={0}
											step={1}
											precision={0}
											style={{ width: '100%' }}
										/>
									</Form.Item>
								</Col>
								{item.partsbadger_production_method == 'Swiss' ? (
									<Col span={24}>
										<div className={'m-2'}>
											If any bars are being returned to inventory, please
											provide the details below:
										</div>
										<Form.List name="returned_bars">
											{(fields, { add, remove }) => (
												<>
													{fields.map(({ key, name, ...restField }) => (
														<Row
															key={key}
															gutter={16}
															align="top"
															style={{ marginBottom: 8 }}
														>
															<Col span={8}>
																<Form.Item
																	{...restField}
																	label="Type of Return"
																	name={[name, 'type_of_return']}
																	rules={[
																		{
																			required: true,
																			message: 'Type of return is required',
																		},
																	]}
																>
																	<Select
																		allowClear
																		options={[
																			{ label: 'Full Bar', value: 'FullBar' },
																			{
																				label: 'Partial Bar',
																				value: 'PartialBar',
																			},
																		]}
																	/>
																</Form.Item>
															</Col>

															<Col span={8}>
																<Form.Item
																	{...restField}
																	label="Quantity"
																	name={[name, 'quantity']}
																	rules={[
																		{
																			required: true,
																			message: 'Quantity is required',
																		},
																		{
																			validator: (_, value) => {
																				if (
																					value === undefined ||
																					value === null ||
																					value === ''
																				)
																					return Promise.resolve();
																				const num = Number(value);
																				if (!Number.isInteger(num) || num < 1) {
																					return Promise.reject(
																						new Error(
																							'Quantity must be a whole number (no decimals).'
																						)
																					);
																				}
																				return Promise.resolve();
																			},
																		},
																	]}
																>
																	<InputNumber
																		min={1}
																		max={99}
																		step={1}
																		precision={0}
																		placeholder="Whole numbers only"
																		style={{ width: '100%' }}
																	/>
																</Form.Item>
															</Col>

															<Col span={6}>
																<Form.Item
																	{...restField}
																	label="Length Returned (in)"
																	name={[name, 'length_returned']}
																	rules={[
																		{
																			required: true,
																			message: 'Length returned is required',
																		},
																		{
																			validator: (_, value) => {
																				if (
																					value === undefined ||
																					value === null ||
																					value === ''
																				)
																					return Promise.resolve();
																				const num = Number(value);
																				if (!Number.isInteger(num) || num < 1) {
																					return Promise.reject(
																						new Error(
																							'Length must be a whole number (no decimals).'
																						)
																					);
																				}
																				return Promise.resolve();
																			},
																		},
																	]}
																>
																	<InputNumber
																		min={1}
																		max={144}
																		step={1}
																		precision={0}
																		placeholder="Whole numbers only"
																		style={{ width: '100%' }}
																	/>
																</Form.Item>
															</Col>

															<Col
																span={2}
																style={{
																	display: 'flex',
																	alignItems: 'center',
																	marginTop: 30,
																}}
															>
																<CloseOutlined
																	onClick={() => remove(name)}
																	style={{ color: '#ff4d4f', fontSize: 16 }}
																/>
															</Col>
														</Row>
													))}

													<Form.Item>
														<Button
															type="dashed"
															onClick={() => add()}
															block
															icon={<PlusOutlined />}
														>
															Add Returned Bar
														</Button>
													</Form.Item>
												</>
											)}
										</Form.List>
									</Col>
								) : (
									<Col span={12}>
										<Form.Item
											label="Quantity Material Returned"
											rules={[
												{
													required: true,
													message: 'Please input the blank parts quantity!',
												},
												{
													validator: (_, value) => {
														if (
															value === undefined ||
															value === null ||
															value === ''
														) {
															return Promise.resolve();
														}
														const num = Number(value);
														if (!Number.isInteger(num) || num < 0) {
															return Promise.reject(
																new Error(
																	'Quantity must be a whole number (no decimals).'
																)
															);
														}
														return Promise.resolve();
													},
												},
											]}
											name="returned_quantity"
										>
											<InputNumber
												min={0}
												step={1}
												precision={0}
												placeholder="Whole numbers only"
												style={{ width: '100%' }}
											/>
										</Form.Item>

										{materialReservationsData && returnedQuantity > 0 && (
											<Form.Item
												name="is_scrap"
												valuePropName="checked"
												initialValue={
													materialReservationsData.assigned_location === 'SCRAP'
												}
											>
												<Checkbox>Scrap</Checkbox>
											</Form.Item>
										)}
									</Col>
								)}
							</Row>
						</Form>
					</Col>
				</Row>
			</Modal>
			<Modal
				open={isConfirmVisible}
				title="FOD (Foreign Object Debris) Declaration"
				onCancel={() => {
					setIsConfirmVisible(false);
					setIsConfirmed(false);
				}}
				onOk={handleConfirmSubmit}
				okButtonProps={{ disabled: !isConfirmed, loading: isSaving }}
				width={700}
			>
				<Text>
					I, a qualified employee of PartsBadger Production, attest that the
					designated FOD area and completed customer parts are free of foreign
					objects, debris, and contaminants, including but not limited to:
				</Text>
				<ul
					style={{
						paddingLeft: 24,
						marginBottom: 16,
						listStyleType: 'disc',
						listStylePosition: 'outside',
					}}
				>
					<li>Burrs, metal chips, and shavings</li>
					<li>Chemicals and/or residue</li>
					<li>Tools, writing utensils, and personal items</li>
					<li>Food and drinks</li>
					<li>Raw material, unfinished parts, and scrap</li>
					<li>
						Any other contaminant or foreign matter other than cleaned Customer
						Parts and Clean “Completed Parts” bins.
					</li>
				</ul>

				<Text strong>
					By checking this box, you acknowledge the statement above is true.
				</Text>
				<Form.Item style={{ marginTop: 16 }}>
					<Checkbox
						checked={isConfirmed}
						onChange={(e) => setIsConfirmed(e.target.checked)}
					>
						I confirm that the above statement is accurate.
					</Checkbox>
				</Form.Item>
			</Modal>
		</>
	);
};

const ItemCard: React.FC<IProps> = ({ item, refetch }) => {
	const [isModalVisible, setIsModalVisible] = useState(false);

	return (
		<Card
			style={{ width: '100%' }}
			cover={
				<img
					className="flex m-auto"
					style={{ width: 180, height: 180, objectFit: 'contain' }}
					alt="part-img"
					src={item.thumbnail_file}
				/>
			}
			actions={[
				<Button
					key="ship"
					onClick={() => setIsModalVisible(true)}
					style={{ width: '100%', height: 42 }}
					type="text"
				>
					<TruckFilled style={{ fontSize: 20 }} />
				</Button>,
			]}
		>
			{isModalVisible && (
				<ShippingModal
					item={item}
					handleCancel={() => setIsModalVisible(false)}
					handleSuccess={() => {
						setIsModalVisible(false);
						if (refetch) {
							refetch();
						}
					}}
				/>
			)}

			<div
				style={{
					borderTop: '1px solid #e0e0e0',
					paddingTop: 16,
				}}
			>
				<Meta
					title={item.name}
					style={{
						textAlign: 'center',
						overflow: 'hidden',
					}}
					className="flex flex-col items-center text-center"
					description={
						<div className="flex flex-col gap-2 items-center">
							<span style={{ fontSize: 16 }}>{item.order.name}</span>
							<span style={{ fontSize: 16, color: '#888' }}>
								Date: {shopDueDate(item)}
							</span>
							<span style={{ fontSize: 16 }}>
								Delivered: {item.delivered_quantity} / {item.quantity}
							</span>
							<Tag color={item.remaining_quantity == 0 ? 'blue' : 'green'}>
								{item.remaining_quantity ? 'Pending' : 'Completed'}
							</Tag>
							<OpenBatchNotice
								itemId={item.id}
								thisJobFull={jobIsFull(item)}
							/>
							{item.requires_inspection_sample_labeling && (
								<InspectionSampleLabelingTag />
							)}
							{item.cleaning_requirement && (
								<CleaningRequirementTag
									cleaning={item.cleaning_requirement}
								/>
							)}
						</div>
					}
				/>
			</div>
		</Card>
	);
};

export default ItemCard;
